import React, { memo, useEffect, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'

import { PageLayoutContainer } from '../../../lib/page-layout-container'
import {
    AuthenticationServiceContextRequest,
    PaymentGetSubscriptionsByOrgApiArg,
    useEntitlementsCreateContextTokenMutation,
    useLazyPaymentGetSubscriptionsByOrgQuery,
} from '../../../common/redux/api/web-api'
import { RootState } from '../../redux/store'
import { LandingMessageSuccessIcon } from '../../../lib/landing-message-success-icon'
import useText from '../../../common/hooks/useText'
import { getFailureReasonFromError } from '../../../common/services/utilsService'
import {
    clearEntitlements,
    normalizeEntitlementsState,
    setEntitlements,
} from '../../../common/redux/features/entitlements/entitlementsSlice'
import { EntitlementsEnum, hasAccessToEntitlement } from '../../../lib/entitlementUtils'

export type BillingSuccessPageProps = {
    className?: ClassName
    style?: React.CSSProperties
}

export const BillingSuccessPage = memo(function BillingSuccessPage({
    className,
    style,
}: BillingSuccessPageProps): React.ReactElement {
    const navigate = useNavigate()
    const text = useText()

    const POLL_INTERVAL_MS = 3000
    const POLL_TIMEOUT_MS = 60_000

    const dispatch = useDispatch()
    const { selectedCompanyId } = useSelector((state: RootState) => state.user)

    const [getActiveSubscriptions] = useLazyPaymentGetSubscriptionsByOrgQuery()
    const [getEntitlements] = useEntitlementsCreateContextTokenMutation()

    const [fulfillmentComplete, setFulfillmentComplete] = useState(false)

    useEffect(() => {
        let cancelled = false
        let hasNavigatedToFailed = false

        const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

        const navigateFailed = (reason?: string) => {
            if (hasNavigatedToFailed) return
            hasNavigatedToFailed = true

            const query = reason ? `?reason=${encodeURIComponent(reason)}` : ''
            navigate(`/billing/failed${query}`, { replace: true })
        }

        const onTimeout = () => navigateFailed('timeout')

        const pollSubscriptions = async () => {
            const start = Date.now()

            do {
                try {
                    const subscriptionsQueryArg = { status: 'active' } as unknown as PaymentGetSubscriptionsByOrgApiArg
                    const activeSubscriptions = await getActiveSubscriptions(subscriptionsQueryArg).unwrap()

                    if (cancelled) return false

                    if (activeSubscriptions.some((s) => s.status === 'active')) return true
                } catch (err) {
                    if (cancelled) return false

                    navigateFailed(getFailureReasonFromError(err))

                    return false
                }
                await sleep(POLL_INTERVAL_MS)
            } while (!cancelled && Date.now() - start < POLL_TIMEOUT_MS)

            return false
        }

        const pollEntitlements = async () => {
            const start = Date.now()

            while (!cancelled && Date.now() - start < POLL_TIMEOUT_MS) {
                try {
                    const authenticationServiceContextRequest: AuthenticationServiceContextRequest = {
                        organizationId: selectedCompanyId,
                    }

                    const entitlements = await getEntitlements({ authenticationServiceContextRequest }).unwrap()

                    if (cancelled) return false

                    const entitlementsState = normalizeEntitlementsState(entitlements, selectedCompanyId)
                    dispatch(setEntitlements(entitlementsState))

                    if (hasAccessToEntitlement(EntitlementsEnum.AUTOMATED_REPORTS, entitlementsState)) {
                        setFulfillmentComplete(true)

                        return true
                    }
                } catch (err) {
                    if (cancelled) return false

                    dispatch(clearEntitlements())
                    navigateFailed(getFailureReasonFromError(err))

                    return false
                }

                await sleep(POLL_INTERVAL_MS)
            }

            return false
        }

        ;(async () => {
            try {
                const okSubs = await pollSubscriptions()
                if (cancelled) return
                if (!okSubs) {
                    if (!hasNavigatedToFailed) onTimeout()
                    return
                }

                const okEnt = await pollEntitlements()
                if (cancelled) return
                if (!okEnt) {
                    if (!hasNavigatedToFailed) onTimeout()
                    return
                }
            } catch {
                if (!cancelled) navigateFailed('unknown')
            }
        })()

        return () => {
            cancelled = true
        }
    }, [dispatch, getActiveSubscriptions, getEntitlements, navigate, selectedCompanyId])

    return (
        <PageLayoutContainer className={cn(className)} style={style}>
            <div
                className="flex justify-content-center align-items-center flex-column gap-4"
                style={{ height: '33vh' }}
            >
                {!fulfillmentComplete ? (
                    <>
                        <h4>{text('lblBillingProcessing')}</h4>

                        <p className="text-xl">
                            {text('lblBillingProcessingDescription', { time: (POLL_TIMEOUT_MS * 2) / 60000 })}
                        </p>

                        <ProgressSpinner />
                    </>
                ) : (
                    <>
                        <LandingMessageSuccessIcon />

                        <h4>{text('lblBillingPlanActivatedSuccessfully')}</h4>

                        <Link data-testid="linkBtn" to={'/billing'}>
                            {text('lblBackToRoute', { route: text('lblBilling') })}
                        </Link>
                    </>
                )}
            </div>
        </PageLayoutContainer>
    )
})
